/**
 * Seasons list (route /seasons). The host picks a venue, sees its seasons
 * and creates a new one. A row opens the season standings. "Season
 * defaults" sets the starting settings for new seasons at the venue.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Flex, Heading, Table, Text } from '@radix-ui/themes';
import VenuePicker from '../Teams/VenuePicker';
import { fetchVenueOptions, type VenueOption } from '../Teams/types';
import { describeRule, fmtSeasonDate } from '../../utils/seasons';
import SeasonForm from './SeasonForm';
import { fetchSeasons, type Season } from './seasonsApi';
import '../Teams/Teams.css';

function seasonStatus(s: Season, now = new Date()): { label: string; color: 'green' | 'gray' | 'blue' } {
  if (new Date(s.endDate) < now) return { label: 'Finished', color: 'gray' };
  if (new Date(s.startDate) > now) return { label: 'Upcoming', color: 'blue' };
  return { label: 'Running', color: 'green' };
}

const SeasonsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [venueId, setVenueId] = useState<number | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchVenueOptions()
      .then((vs) => {
        if (!active) return;
        setVenues(vs);
        const fromUrl = Number(searchParams.get('venueId'));
        if (fromUrl && vs.some((v) => v.id === fromUrl)) setVenueId(fromUrl);
        else if (vs[0]) setVenueId(vs[0].id);
        else setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError('Failed to load venues.');
        setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async (vid: number) => {
    setLoading(true);
    setError(null);
    try {
      setSeasons(await fetchSeasons(vid));
    } catch {
      setError('Failed to load seasons for this venue.');
      setSeasons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (venueId !== null) {
      load(venueId);
      setSearchParams({ venueId: String(venueId) }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, load]);

  return (
    <div className="teams-page">
      <div className="teams-header">
        <div>
          <Heading size="6">Seasons</Heading>
          <Text size="2" color="gray">
            League standings across the game nights at a venue.
          </Text>
        </div>
        <Flex gap="2" wrap="wrap">
          <Button variant="soft" onClick={() => setDefaultsOpen(true)} disabled={venueId === null}>
            Season defaults
          </Button>
          <Button onClick={() => setFormOpen(true)} disabled={venueId === null}>
            + New season
          </Button>
        </Flex>
      </div>

      <div className="teams-toolbar">
        <Text size="2" color="gray">Venue:</Text>
        <VenuePicker venues={venues} value={venueId} onChange={setVenueId} />
      </div>

      {error && <div className="teams-error">{error}</div>}

      {loading ? (
        <div className="teams-empty">Loading seasons…</div>
      ) : seasons.length === 0 ? (
        <div className="teams-empty">
          <Text as="p" size="3" weight="medium">No seasons yet for this venue.</Text>
          <Text as="p" size="2" color="gray">
            Create a season to rank teams across several game nights.
          </Text>
        </div>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Season</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Dates</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Scoring</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Status</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {seasons.map((s) => {
              const status = seasonStatus(s);
              return (
                <Table.Row key={s.id} className="teams-row-clickable" onClick={() => navigate(`/seasons/${s.id}`)}>
                  <Table.RowHeaderCell>
                    <Text weight="medium">{s.name}</Text>
                    {!s.isPublic && (
                      <Badge ml="2" color="gray" variant="soft">Private</Badge>
                    )}
                  </Table.RowHeaderCell>
                  <Table.Cell>
                    {fmtSeasonDate(s.startDate)} – {fmtSeasonDate(s.endDate)}
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" color="gray">{describeRule(s)}</Text>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Badge color={status.color} variant="soft">{status.label}</Badge>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}

      <SeasonForm
        open={formOpen}
        onOpenChange={setFormOpen}
        venues={venues}
        defaultVenueId={venueId}
        onSaved={(s) => s && navigate(`/seasons/${s.id}`)}
      />
      <SeasonForm
        kind="venueDefaults"
        open={defaultsOpen}
        onOpenChange={setDefaultsOpen}
        venues={venues}
        defaultVenueId={venueId}
        onSaved={() => {}}
      />
    </div>
  );
};

export default SeasonsList;
